import { ReactElement, useCallback } from 'react';
import { createFetchContext } from '../../../../components/contexts/FetchContextFactory';
import DisplayError from '../../../../components/info/DisplayError';
import Loading from '../../../../components/info/Loading';
import { Teacher } from '../types';
import { getSearchedUserByIdentification } from '../api';
import { useTexts } from '../hooks/useTexts';

export interface TableWithUserProps {
  defaultIdentificationType: string;
  children: ReactElement;
}

const { FetchProvider, FetchConsumer, useFetch } = createFetchContext<
  {
    identificationTypeId: string;
    identification: string;
    countryId: string | null;
  },
  Teacher
>();
export const useAttendancesViewFetch = useFetch;

export function AttendancesViewLoader({
  defaultIdentificationType,
  children,
}: TableWithUserProps) {
  const request = useCallback((query) => {
    return getSearchedUserByIdentification(
      query.identificationTypeId,
      query.identification,
      query.countryId,
    );
  }, []);
  return (
    <FetchProvider
      request={request}
      defaultQuery={{
        identificationTypeId: defaultIdentificationType,
        identification: '',
        countryId: null,
      }}
    >
      {children}
    </FetchProvider>
  );
}

export function AttendancesViewConsumer({
  children,
}: {
  children: (props: { teacher: Teacher }) => ReactElement;
}) {
  const texts = useTexts();
  return (
    <FetchConsumer>
      {({ data, loading, error, refresh }) => {
        if (error) {
          return (
            <DisplayError
              insideCard
              textBody={error.code}
              retryAction={refresh}
              loadingAction={loading}
            />
          );
        }

        if (loading) {
          return (
            <div className="mx-3">
              <Loading insideCard />
            </div>
          );
        }
        if (!data) {
          return (
            <DisplayError
              insideCard
              title={texts.infoAndError.userNotFoundError.title}
              textBody={texts.infoAndError.userNotFoundError.body}
            />
          );
        }

        return children({ teacher: data });
      }}
    </FetchConsumer>
  );
}
